var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { DebounceInput } from 'react-debounce-input';
import { inputChange, useCitrusForm } from '@citrus/form';
import { Input, styles as inputStyles, citrus2022Styles as inputStylesCitrus2022, validationAccessibility, } from '@citrus/input';
import { getAutomation, getDebug, useCitrusDebug, getStyleNames, useFormInputId, useUpdateValidation, useUserManagedValidation, useValidationOnMount, } from '@citrus/runtime';
import { isNilOrEmpty, isNotNilOrEmpty, enterKeyPressed, keyPressed, only, isString, isNumber } from '@pcty/utils';
import { getThemeStyle, useTheme } from '@citrus/user';
import { ComponentWrapper, MinusButton, PlusButton } from './NumberStepper.styled';
import { focus } from '../styles/citron';
import { processValue, shouldDecrementBeEnabled, shouldIncrementBeEnabled } from './private/helpers';
var VERSION = '8.3.0';
var NumberStepper = function (_a) {
    var _b, _c;
    var _d, _e, _f, _g;
    var _h = _a.ariaLabelIncrease, ariaLabelIncrease = _h === void 0 ? 'increase' : _h, _j = _a.ariaLabelReduce, ariaLabelReduce = _j === void 0 ? 'reduce' : _j, automationId = _a.automationId, 
    // FIXME: check if `children` is needed
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    children = _a.children, className = _a.className, _k = _a.classNameWrapper, classNameWrapper = _k === void 0 ? '' : _k, _l = _a.contentEditable, contentEditable = _l === void 0 ? false : _l, css = _a.css, _m = _a.debounceTimeout, debounceTimeout = _m === void 0 ? 300 : _m, _o = _a.decrementor, decrementor = _o === void 0 ? function (x) { return x - 1; } : _o, disabled = _a.disabled, _p = _a.emptyLabel, emptyLabel = _p === void 0 ? false : _p, errorMessage = _a.errorMessage, footnote = _a.footnote, _q = _a.formatter, formatter = _q === void 0 ? function (x) { return x; } : _q, _r = _a.hideOptionalLabel, hideOptionalLabel = _r === void 0 ? false : _r, id = _a.id, _s = _a.incrementor, incrementor = _s === void 0 ? function (x) { return x + 1; } : _s, label = _a.label, labelAddOn = _a.labelAddOn, labelPosition = _a.labelPosition, max = _a.max, min = _a.min, onChange = _a.onChange, _t = _a.required, required = _t === void 0 ? false : _t, _u = _a.showRequiredLabel, showRequiredLabel = _u === void 0 ? false : _u, showValidationState = _a.showValidationState, tooltip = _a.tooltip, valid = _a.valid, validate = _a.validate, _v = _a.value, value = _v === void 0 ? 0 : _v, _w = _a.__TYPE, __TYPE = _w === void 0 ? 'NumberStepper' : _w, other = __rest(_a, ["ariaLabelIncrease", "ariaLabelReduce", "automationId", "children", "className", "classNameWrapper", "contentEditable", "css", "debounceTimeout", "decrementor", "disabled", "emptyLabel", "errorMessage", "footnote", "formatter", "hideOptionalLabel", "id", "incrementor", "label", "labelAddOn", "labelPosition", "max", "min", "onChange", "required", "showRequiredLabel", "showValidationState", "tooltip", "valid", "validate", "value", "__TYPE"]);
    var _x = useCitrusForm(), form = _x.form, dispatch = _x.dispatch, customValidate = _x.validate, formDisabled = _x.disabled;
    var debug = useCitrusDebug();
    var theme = useTheme();
    var isCitron = (_d = theme.citrus2022) !== null && _d !== void 0 ? _d : false;
    var isRequired = isString(required) ? true : required !== null && required !== void 0 ? required : false;
    var requiredMessage = isString(required) ? required : undefined;
    // #region state
    var inputRef = useRef(null);
    // NOTE: 'incrementor' and 'decrementor' were originally misspelled. This
    //        will correct that without having to make a breaking change.
    var _incrementor = useRef(incrementor !== null && incrementor !== void 0 ? incrementor : other.incrementer);
    var _decrementor = useRef(decrementor !== null && decrementor !== void 0 ? decrementor : other.decrementer);
    var _y = useState(false), hasFocus = _y[0], setHasFocus = _y[1];
    var _z = useState(false), hasReadOnlyFocus = _z[0], setHasReadOnlyFocus = _z[1];
    var _0 = useState(value), _value = _0[0], setValue = _0[1];
    var _1 = useState(formatter(value)), displayValue = _1[0], setDisplayValue = _1[1];
    var _2 = useState({ valid: false }), _validation = _2[0], setValidation = _2[1];
    var formInputId = useFormInputId(id);
    // casting params because they are optional in the hook, even though they are typed as required
    var userManagedValidation = useUserManagedValidation(valid, errorMessage, showValidationState, setValidation);
    // #endregion state
    // #region callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var updateForm = useCallback(inputChange(dispatch, customValidate), [customValidate]);
    var style = useCallback(function (legacy, citrus2022) {
        return getThemeStyle(theme, { legacy: legacy, citrus2022: citrus2022 });
    }, [theme]);
    var validateRequired = useCallback(function (value) {
        if (isNilOrEmpty(value)) {
            return {
                valid: false,
                errorMessage: requiredMessage !== null && requiredMessage !== void 0 ? requiredMessage : "".concat(label !== null && label !== void 0 ? label : 'This', " is required"),
            };
        }
        return { valid: true };
    }, [label, requiredMessage]);
    var getValidation = useCallback(function (value) {
        if (userManagedValidation && !validate)
            return { valid: valid, errorMessage: errorMessage };
        var validation = { valid: true };
        if (isRequired) {
            validation = validateRequired(value);
        }
        if (validate && validation.valid) {
            validation = validate(value, {
                id: id,
                label: label,
                required: isRequired,
            });
        }
        return validation;
    }, [errorMessage, id, isRequired, label, userManagedValidation, valid, validate, validateRequired]);
    var handleChange = useCallback(function (value) {
        value = processValue(value, min, max);
        var formatted = formatter(value);
        setValue(value);
        setDisplayValue(formatted);
        var validation = getValidation(value);
        setValidation(only(validation, ['valid', 'errorMessage']));
        onChange === null || onChange === void 0 ? void 0 : onChange({ id: id, value: value, formatted: formatted, validation: validation });
        if (!userManagedValidation) {
            updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: true }));
        }
    }, [formInputId, formatter, getValidation, id, label, max, min, onChange, updateForm, userManagedValidation]);
    var handleFocus = useCallback(function () {
        if (contentEditable) {
            setHasFocus(true);
        }
        else {
            setHasReadOnlyFocus(true);
        }
    }, [contentEditable]);
    var handleBlur = useCallback(function (e) {
        if (!contentEditable) {
            setHasReadOnlyFocus(false);
            return;
        }
        var value = processValue(e.target.value, min, max);
        var displayValue = formatter(processValue(e.target.value.toString().replace(/,/g, ''), min, max));
        setHasFocus(false);
        setValue(value);
        setDisplayValue(displayValue);
        var validation = getValidation(value);
        setValidation(only(validation, ['valid', 'errorMessage']));
        onChange === null || onChange === void 0 ? void 0 : onChange({ id: id, value: value, formatted: displayValue, validation: validation });
        if (!userManagedValidation) {
            updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: true }));
        }
    }, [
        contentEditable,
        formInputId,
        formatter,
        getValidation,
        id,
        label,
        max,
        min,
        onChange,
        updateForm,
        userManagedValidation,
    ]);
    var handleKeyPress = function (e) {
        // Enter
        if (enterKeyPressed(e)) {
            e.target.blur();
            handleBlur(e);
            e.preventDefault();
            return;
        }
        if (!contentEditable) {
            var isDecrementEnabled_1 = shouldDecrementBeEnabled(disabled, formDisabled, _value, min);
            var isIncrementEnabled_1 = shouldIncrementBeEnabled(disabled, formDisabled, _value, max);
            // if not contentEditable, no risk of empty string
            var value_ = _value;
            // Up Arrow, Right Arrow, Plus Key
            if (isIncrementEnabled_1 && keyPressed(e, ['ArrowUp', 'ArrowRight', 'NumpadAdd', 'Equal'])) {
                handleChange(_incrementor.current(value_));
                e.preventDefault();
                return;
            }
            // Down Arrow, Left Arrow, Minus Key
            if (isDecrementEnabled_1 && keyPressed(e, ['ArrowDown', 'ArrowLeft', 'NumpadSubtract', 'Minus'])) {
                handleChange(_decrementor.current(value_));
                e.preventDefault();
                return;
            }
        }
    };
    // #endregion callbacks
    // #region effects
    // casting label because it is a required param, but it's not used in this instance
    useValidationOnMount(id, label, value, userManagedValidation, getValidation, setValidation, updateForm);
    // safe to cast because the hook accounts for the possibility of undefined
    useUpdateValidation(form, formInputId, valid, errorMessage, _validation, setValidation);
    // if required changes after mount, revalidate
    useEffect(function () {
        var _a, _b, _c;
        var validation = getValidation(value);
        setValidation(validation);
        updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: (_c = (_b = (_a = form === null || form === void 0 ? void 0 : form.inputs) === null || _a === void 0 ? void 0 : _a[formInputId]) === null || _b === void 0 ? void 0 : _b.dirty) !== null && _c !== void 0 ? _c : false }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [required]);
    useEffect(function () {
        setValue(value);
    }, [value]);
    useEffect(function () {
        setDisplayValue(formatter(isNumber(_value) ? _value : Number(_value)));
    }, [_value, formatter]);
    useEffect(function () {
        var _a, _b;
        (_a = _incrementor.current === incrementor) !== null && _a !== void 0 ? _a : other.incrementer;
        (_b = _decrementor.current === decrementor) !== null && _b !== void 0 ? _b : other.decrementer;
    }, [other.incrementer, other.decrementer, incrementor, decrementor]);
    var handleInputChange = useCallback(function (e) {
        setValue(e.target.value);
    }, []);
    // #endregion effects
    var isDecrementEnabled = shouldDecrementBeEnabled(disabled, formDisabled, _value, min);
    var isIncrementEnabled = shouldIncrementBeEnabled(disabled, formDisabled, _value, max);
    var incAutomationId = (automationId && "".concat(automationId, "-increment")) || null;
    var decAutomationId = (automationId && "".concat(automationId, "-decrement")) || null;
    var _displayValue = hasFocus ? _value : displayValue;
    var focusStyles = style(inputStyles.focus, focus);
    var errorStyles = style(inputStyles.error, inputStylesCitrus2022.error);
    var disabledStyles = style(inputStyles.disabled, inputStylesCitrus2022.disabled);
    var emotionObj = (_b = {},
        _b[emotionCss(focusStyles)] = hasFocus || hasReadOnlyFocus,
        _b[emotionCss(errorStyles)] = !_validation.valid && (showValidationState || (form === null || form === void 0 ? void 0 : form.showValidationState)),
        _b[emotionCss(disabledStyles)] = disabled !== null && disabled !== void 0 ? disabled : formDisabled,
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        focus: (_e = emotionObj[emotionCss(focusStyles)]) !== null && _e !== void 0 ? _e : false,
        error: (_f = emotionObj[emotionCss(errorStyles)]) !== null && _f !== void 0 ? _f : false,
        disabled: (_g = emotionObj[emotionCss(disabledStyles)]) !== null && _g !== void 0 ? _g : false,
    };
    var _showValidationState = showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState;
    return (React.createElement(Input, __assign({ automationId: automationId, className: classNameWrapper, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, emptyLabel: emptyLabel, errorMessage: _validation.errorMessage, footnote: footnote, hideOptionalLabel: hideOptionalLabel, id: id, label: label, labelAddOn: labelAddOn, labelPosition: labelPosition, required: isRequired, showRequiredLabel: showRequiredLabel, showValidationState: _showValidationState, tooltip: tooltip, valid: _validation.valid }, other),
        React.createElement(ComponentWrapper, __assign({}, getAutomation(automationId), getDebug(debug, __TYPE, VERSION), validationAccessibility(id, _validation.valid, _showValidationState !== null && _showValidationState !== void 0 ? _showValidationState : false), { className: classes, id: "".concat(id, "-wrapper"), styleNames: getStyleNames(styleNames) }),
            React.createElement(DebounceInput, __assign({ "aria-atomic": true, "aria-labelledby": "".concat(id, "-for-labelledby"), "aria-live": "polite", className: cx((_c = {},
                    _c[emotionCss(isCitron ? inputStylesCitrus2022.value : inputStyles.value)] = true,
                    _c)), debounceTimeout: debounceTimeout, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, forceNotifyByEnter: true, forceNotifyOnBlur: true, id: id, inputRef: inputRef, onBlur: handleBlur, onChange: handleInputChange, onFocus: handleFocus, onKeyDown: handleKeyPress, readOnly: !contentEditable, required: isRequired, type: "text", value: _displayValue }, getAutomation(automationId, 'input'))),
            React.createElement(MinusButton, { "aria-label": ariaLabelReduce, automationId: decAutomationId, buttonType: "button", disabled: !isDecrementEnabled || (disabled !== null && disabled !== void 0 ? disabled : formDisabled), id: "".concat(id, "-button-decrement"), onClick: function () {
                    return isDecrementEnabled && handleChange(_decrementor.current(_value === '' ? 0 : _value));
                } }),
            React.createElement(PlusButton, { "aria-label": ariaLabelIncrease, automationId: incAutomationId, buttonType: "button", disabled: !isIncrementEnabled || (disabled !== null && disabled !== void 0 ? disabled : formDisabled), id: "".concat(id, "-button-increment"), onClick: function () {
                    return isIncrementEnabled && handleChange(_incrementor.current(_value === '' ? 0 : _value));
                } }))));
};
export default NumberStepper;
